// src/component/SekaiMuraVerificationCard.jsx
import React, { useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Paper,
  Box,
  Typography,
  Divider,
  Alert,
  TextField,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const functions = getFunctions();
const verifySekaiMuraToken = httpsCallable(functions, "verifySekaiMuraToken");

export default function SekaiMuraVerificationCard({
  user,
  accent = "#5A35FF",
  showSnack,
  disabled = false,
}) {
  const [sekaiMuraToken, setSekaiMuraToken] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // ★ onSnapshot 反映待ちの“空白”を消す（成功を即時表示）
  const [optimisticSekaimura, setOptimisticSekaimura] = useState(false);

  const isSekaimura = optimisticSekaimura || !!user?.isSekaimura;

  const helperText = useMemo(() => {
    if (isSekaimura) return "セカイムラメンバー向けの機能が開放されています。";
    return "セカイムラのトークン入力で、限定機能を開放します。";
  }, [isSekaimura]);

  const handleSekaiMuraVerify = async () => {
    if (disabled || loading) return;

    const trimmed = (sekaiMuraToken || "").trim();
    if (!trimmed) {
      setMessage("セカイムラのトークンを入力してください。");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await verifySekaiMuraToken({ token: trimmed });

      // ★ 即時に成功表示（onSnapshot待ち不要）
      setOptimisticSekaimura(true);

      setMessage("セカイムラ認証が完了しました。");
      showSnack?.("セカイムラ認証が完了しました。");
      setSekaiMuraToken("");
    } catch (e) {
      console.error("verifySekaiMuraToken error:", e);

      // Callable error code は "functions/xxx"
      if (e?.code === "functions/failed-precondition") {
        setMessage("このトークンは既に使用済みです。");
      } else if (e?.code === "functions/not-found") {
        setMessage("無効なセカイムラトークンです。");
      } else if (e?.code === "functions/unauthenticated") {
        setMessage("ログインしてください。");
      } else if (e?.code === "functions/invalid-argument") {
        setMessage("トークンが不正です。");
      } else {
        setMessage("セカイムラ認証中にエラーが発生しました。");
      }
    } finally {
      setLoading(false);
    }
  };

  const FeaturesIntro = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {isSekaimura
          ? "以下の機能がご利用いただけます。"
          : "セカイムラ認証後に以下の機能が利用できます。"}
      </Typography>

      <List dense sx={{ py: 0 }}>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ChatBubbleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="一部チャット"
            secondary={
              isSekaimura
                ? "セカイムラメンバーはチャットに参加できます。"
                : "認証後に参加可能になります。"
            }
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        p: { xs: 2, md: 3 },
        mb: 4,
        bgcolor: "rgba(255,255,255,0.98)",
        boxShadow: "0 10px 26px rgba(15,10,40,0.08)",
        border: "1px solid rgba(0,0,0,0.06)",
        opacity: disabled ? 0.6 : 1,
        position: "relative",
      }}
    >
      {/* ★ 全面ローディング（置き去り防止） */}
      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            px: 3,
          }}
        >
          <CircularProgress />
          <Typography sx={{ fontSize: 13, opacity: 0.95 }}>
            認証を確認しています…（数秒かかる場合があります）
          </Typography>
        </Box>
      </Backdrop>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: `${accent}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: accent }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 900 }}>
            セカイムラ認証
          </Typography>
          <Typography sx={{ fontSize: 12, color: "rgba(80,80,110,0.8)" }}>
            {helperText}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {isSekaimura && (
        <Alert severity="success" sx={{ mb: 2 }}>
          あなたはセカイムラメンバー認証がなされています。
        </Alert>
      )}

      {!isSekaimura && (
        <>
          <Typography sx={{ fontSize: 13.5, color: "rgba(40,40,60,0.9)" }}>
            セカイムラのトークンを入力してください。
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
            <TextField
              label="セカイムラのトークン"
              variant="outlined"
              fullWidth
              value={sekaiMuraToken}
              onChange={(e) => setSekaiMuraToken(e.target.value)}
              disabled={disabled || loading}
            />

            <Button
              variant="contained"
              onClick={handleSekaiMuraVerify}
              disabled={disabled || loading}
              sx={{ bgcolor: accent, "&:hover": { bgcolor: accent } }}
            >
              {loading ? "認証中..." : "セカイムラ認証する"}
            </Button>
          </Box>

          {message && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13,
                color: message.includes("完了") ? "success.main" : "error.main",
              }}
            >
              {message}
            </Typography>
          )}
        </>
      )}

      <FeaturesIntro />
    </Paper>
  );
}
